import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCopy, FiShare2, FiSmartphone, FiMail, FiMessageSquare, FiUsers } from 'react-icons/fi';
import QRCode from 'qrcode';
import { toast } from 'react-toastify';
import UserShareModal from './UserShareModal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ShareSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
`;

const URLContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
`;

const URLInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 14px;
  color: #374151;
  outline: none;
  font-family: 'Courier New', monospace;
`;

const CopyButton = styled.button`
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #0284c7;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const QRCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const QRCodeImage = styled.img`
  width: 150px;
  height: 150px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  padding: 8px;
`;

const QRCodeLabel = styled.p`
  margin: 12px 0 0 0;
  font-size: 14px;
  color: #6b7280;
  text-align: center;
`;

const ShareButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  &:active {
    transform: translateY(1px);
  }
`;

const FolderInfo = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
`;

const FolderTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FolderDescription = styled.p`
  font-size: 14px;
  color: #1e40af;
  margin: 0;
  opacity: 0.8;
`;

const FolderStats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 12px;
  color: #1e40af;
  opacity: 0.7;
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FolderShareModal = ({ isOpen, onClose, folder }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);
  const [showUserShareModal, setShowUserShareModal] = useState(false);

  // Generate the shareable URL for the folder
  const shareUrl = folder?.id 
    ? `${window.location.origin}/folder/${folder.id}`
    : '';

  // Generate QR code
  useEffect(() => {
    if (isOpen && folder?.id) {
      setIsGeneratingQR(true);
      QRCode.toDataURL(shareUrl, {
        width: 150,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      .then(url => {
        setQrCodeUrl(url);
        setIsGeneratingQR(false);
      })
      .catch(err => {
        console.error('Error generating QR code:', err);
        setIsGeneratingQR(false);
      });
    }
  }, [isOpen, folder?.id, shareUrl]);

  // Copy URL to clipboard
  const copyToClipboard = (url, label) => {
    navigator.clipboard.writeText(url).then(() => {
      toast.success(`${label} copied to clipboard!`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Share functions
  const shareViaEmail = () => {
    const subject = `Folder: ${folder?.name || 'Untitled Folder'}`;
    const body = `Check out this folder:\n\n${shareUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl);
  };

  const shareViaSMS = () => {
    const body = `Check out this folder: ${shareUrl}`;
    const smsUrl = `sms:?body=${encodeURIComponent(body)}`;
    window.open(smsUrl);
  };

  const shareViaWhatsApp = () => {
    const text = `Check out this folder: ${shareUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareViaTeams = () => {
    const text = `Check out this folder: ${folder?.name || 'Untitled Folder'}`;
    const teamsUrl = `https://teams.microsoft.com/share?href=${encodeURIComponent(shareUrl)}&msgText=${encodeURIComponent(text)}`;
    window.open(teamsUrl, '_blank');
  };

  if (!isOpen || !folder) return null;

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Share Folder</ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <FolderInfo>
          <FolderTitle>
            📁 {folder.name || 'Untitled Folder'}
          </FolderTitle>
          <FolderDescription>
            {folder.description || 'No description provided'}
          </FolderDescription>
          <FolderStats>
            <StatItem>
              <FiUsers size={12} />
              {folder.form_count || 0} forms
            </StatItem>
            <StatItem>
              Created {new Date(folder.created_at).toLocaleDateString()}
            </StatItem>
          </FolderStats>
        </FolderInfo>

        <ShareSection>
          <SectionTitle>Folder Link</SectionTitle>
          <URLContainer>
            <URLInput
              value={shareUrl}
              readOnly
              onClick={(e) => e.target.select()}
            />
            <CopyButton onClick={() => copyToClipboard(shareUrl, 'Folder link')}>
              <FiCopy size={16} />
              Copy
            </CopyButton>
          </URLContainer>
        </ShareSection>

        <ShareSection>
          <SectionTitle>QR Code</SectionTitle>
          <QRCodeContainer>
            {isGeneratingQR ? (
              <div style={{ 
                width: '150px', 
                height: '150px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }}>
                <div style={{ color: '#6b7280' }}>Generating...</div>
              </div>
            ) : (
              <QRCodeImage src={qrCodeUrl} alt="QR Code" />
            )}
            <QRCodeLabel>Scan to open folder on mobile</QRCodeLabel>
          </QRCodeContainer>
        </ShareSection>

        <ShareSection>
          <SectionTitle>Share with Portal Users</SectionTitle>
          <ShareButton 
            onClick={() => setShowUserShareModal(true)}
            style={{ 
              width: '100%', 
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: '600',
              padding: '14px'
            }}
          >
            <FiUsers size={18} />
            Share with Portal Users
          </ShareButton>
          <div style={{ 
            marginTop: '8px', 
            fontSize: '12px', 
            color: '#6b7280',
            textAlign: 'center'
          }}>
            Give specific users access to view, edit, or manage this folder
          </div>
        </ShareSection>

        <ShareSection>
          <SectionTitle>Share Via</SectionTitle>
          <ShareButtons>
            <ShareButton onClick={shareViaEmail}>
              <FiMail size={16} />
              Email
            </ShareButton>
            <ShareButton onClick={shareViaSMS}>
              <FiMessageSquare size={16} />
              SMS
            </ShareButton>
            <ShareButton onClick={shareViaWhatsApp}>
              <FiSmartphone size={16} />
              WhatsApp
            </ShareButton>
            <ShareButton onClick={shareViaTeams}>
              <FiUsers size={16} />
              Teams
            </ShareButton>
          </ShareButtons>
        </ShareSection>
      </ModalContent>

      {showUserShareModal && (
        <UserShareModal
          isOpen={showUserShareModal}
          onClose={() => setShowUserShareModal(false)}
          item={folder}
          type="folder"
        />
      )}
    </ModalOverlay>
  );
};

export default FolderShareModal;
